import { useState, useEffect } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/ui/card";
import { Check, Lock, Sparkles, Upload, Key } from "lucide-react";
import { toast } from "sonner";
import { ALWAYS_INCLUDED, formatFeature } from "../lib/billing";
import { API } from "@/lib/api";
import { doorverwijzing } from "../lib/instellingenadres";
import { useTaal } from "@/contexts/TaalContext";
import { dagVanSeconden } from "@/lib/datum";


const TIER_COLORS = {
  free: "bg-muted/50 border-border",
  pro: "border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5",
  teams: "border-nebula/40 bg-nebula/5",
};

/**
 * G1 (Frank, 18 oktober 2026): de winkel staat in Instellingen, op het tabblad
 * Abonnement, onder het abonnement. Dit blok is de inhoud, inclusief de melding
 * na de terugkeer van Stripe (`?success=1` of `?cancelled=1`).
 */
export function WinkelBlok() {
  const { t, taal } = useTaal();
  const [searchParams] = useSearchParams();
  const [tiers, setTiers] = useState([]);
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(true);

  const success = searchParams.get("success");
  const cancelled = searchParams.get("cancelled");

  useEffect(() => {
    if (success) toast.success(t("Betaling gelukt. De licentie wordt zo geactiveerd."));
    if (cancelled) toast.info(t("Betaling geannuleerd."));
  }, [success, cancelled, t]);

  const fetchData = async () => {
    try {
      const [tiersRes, licRes] = await Promise.all([
        axios.get(`${API}/store/tiers`).catch(() => ({ data: { tiers: [] } })),
        axios.get(`${API}/license/status`).catch(() => ({ data: { tier: "free", features: [] } })),
      ]);
      setTiers(tiersRes.data.tiers || []);
      setLicense(licRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCheckout = async (tier, billing) => {
    try {
      const resp = await axios.post(`${API}/store/checkout`, { tier, billing });
      if (resp.data.url) {
        window.open(resp.data.url, "_blank");
      } else {
        toast.info(resp.data.note || t("Afrekenen gestart"));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("Afrekenen mislukt"));
    }
  };

  const handleActivateLicense = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const text = await file.text();
        const data = JSON.parse(text);
        const resp = await axios.post(`${API}/license/activate`, data);
        if (resp.data.ok) {
          toast.success(t("Licentie geactiveerd: {niveau}", { niveau: resp.data.tier }));
          fetchData();
        } else {
          toast.error(resp.data.error || t("Ongeldige licentie"));
        }
      } catch (err) {
        toast.error(t("Licentiebestand lezen mislukt"));
      }
    };
    input.click();
  };

  if (loading) return <p className="py-6 text-center text-muted-foreground">{t("Laden...")}</p>;

  const isPro = license?.tier !== "free";

  return (
    <div className="space-y-4" data-testid="winkel-blok">
      {/* Current license status */}
      {license && (
        <div className="px-3 py-2 rounded bg-muted/30 text-sm flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Key className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">
              {isPro ? (license.customer_email ? t("Licentie op naam van {wie}", { wie: license.customer_email }) : t("Licentie op jouw naam")) : t("Gratis, helemaal offline, geen licentie nodig")}
            </span>
            {license.expires_at > 0 && (
              <span className="text-xs text-muted-foreground">
                {t("· verloopt op {datum}", { datum: dagVanSeconden(license.expires_at, taal) })}
              </span>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={handleActivateLicense}>
            <Upload className="h-3 w-3 mr-1" />
            {t("Licentie activeren")}
          </Button>
        </div>
      )}

      <div>
        {/* Free section */}
        <div className="mb-8">
          <h2 className="text-sm font-medium text-muted-foreground mb-3 uppercase tracking-wider">{t("Altijd gratis")}</h2>
          <Card className="bg-muted/20 border-border/50">
            <CardContent className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                {[t("1 taalmodel naar keuze"), t("Onbeperkte lokale gespreksgeschiedenis"), t("CLAW-Runtime (alleen lezen: zoeken, bladeren, schermafbeelding)"), t("Basisskills uit Galaxy"), t("Synchronisatie via het lokale netwerk (je eigen apparaten)"), t("Lokale gegevensopslag"), t("Eigen huisstijl (logo en kleuren)")].map(f => (
                  <div key={f} className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-ok shrink-0" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Paid tiers */}
        <h2 className="text-sm font-medium text-muted-foreground mb-3 uppercase tracking-wider">{t("Premium-abonnementen")}</h2>
        <div className="grid gap-4 md:grid-cols-2">
          {tiers.filter(t => t.price_eur > 0).map((tier) => {
            const isActive = license?.tier === tier.key;
            const isUpgrade = license?.tier === "free" || (tier.key === "pro" && license?.tier === "free");

            return (
              <Card key={tier.key} className={`${TIER_COLORS[tier.key]} relative overflow-hidden`}>
                {tier.key === "pro" && (
                  <div className="absolute top-0 right-0 bg-[hsl(var(--accent))] text-[10px] font-bold text-white px-3 py-1 rounded-bl">
                    {t("POPULAIR")}
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{tier.label}</span>
                    <span className="text-2xl font-bold">
                      €{tier.price_eur}
                      <span className="text-sm font-normal text-muted-foreground">{t("/maand")}</span>
                    </span>
                  </CardTitle>
                  <CardDescription>
                    {t("Alles uit Gratis plus premiumfuncties")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    {tier.features.filter(f => !ALWAYS_INCLUDED.includes(f)).map(f => (
                      <li key={f} className="flex items-start gap-2">
                        <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: tier.key === "teams" ? "#a855f7" : "hsl(var(--accent))" }} />
                        <span>{t(formatFeature(f))}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button
                    className="w-full"
                    variant={isActive ? "outline" : "default"}
                    onClick={() => handleCheckout(tier.key, "monthly")}
                    disabled={isActive}
                  >
                    {isActive ? (
                      <><Check className="h-4 w-4 mr-1" /> {t("Actief")}</>
                    ) : (
                      <><Sparkles className="h-4 w-4 mr-1" /> {t("Abonneren")}</>
                    )}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* Self-hosted note */}
        <Card className="mt-6 bg-muted/20 border-dashed">
          <CardContent className="p-4 flex items-center gap-3 text-sm text-muted-foreground">
            <Lock className="h-4 w-4 shrink-0" />
            <span>
              {t("Je node draait volledig op je eigen machine. De licentie wordt hier gecontroleerd, zonder contact met een server. Stripe verwerkt alleen betalingen; er gaan geen gebruiksgegevens van je machine af.")}
            </span>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// G4: de oude route verwijst door naar Instellingen, met de melding van Stripe erbij.
export default function StorePage() {
  const location = useLocation();
  return <Navigate to={doorverwijzing("/store", location.search)} replace />;
}
